import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
  SxProps,
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { GridColDef } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import {
  getEventsDataGrid,
  getEventsMessaageResponseRate,
  getEventsOverview,
} from "../api/requests/dashboard";
import { getTagResponsesByEventMsgId } from "../api/requests/events";
import EventTagPieChart from "./EventTagPieChart";
import UserAnalyticsTable from "./UserAnalyticsTable";
import { PaginationType } from "./Groups";
import { formatFullDateTime } from "../utils/utils";
import {
  breadcrumbActiveStyles,
  breadcrumbIntitalStyles,
  breadcrumbMainStyles,
  gridStyles,
  mainGridStyles,
  mainScrollContent,
  overviewHeader,
  titleSectionStyles,
  viewButtonStyles,
} from "./mandatoryMessageDashboard";
import { BASE } from "../routes/routePaths";
import {
  titleDescriptionStyles,
  titleStyles,
} from "../components/CommDataGrid";
import { viewReciepientStyles } from "./MessageStatsTable";
import { mainContentStyles } from "./Dashboard";

const StatCard = ({
  label,
  value,
  backgroundColor,
  color,
}: {
  label: string;
  value: string | number;
  backgroundColor?: string;
  color?: string;
}) => (
  <Paper
    elevation={2}
    sx={{
      padding: 2,
      textAlign: "center",
      backgroundColor: backgroundColor || "#fff",
      boxShadow: "none",
    }}
  >
    <Typography variant="subtitle2">{label}</Typography>
    <Typography fontWeight={600} variant="h5" sx={{ color: color }}>
      {value}
    </Typography>
  </Paper>
);

interface EventOverviewDataType {
  totalEvents: number;
  totalEventMessages: number;
  upcomingEvents: number;
  pastEvents: number;
  activeEvents: number;
}

const EventMessageDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [openPieChart, setOpenPieChart] = useState<boolean>(false);
  const [eventsOverviewData, setEventsOverviewData] =
    useState<EventOverviewDataType>({
      totalEvents: 0,
      totalEventMessages: 0,
      upcomingEvents: 0,
      pastEvents: 0,
      activeEvents: 0,
    });
  const [messageResponseData, setMessageResponseData] = useState({
    recipientsCount: 0,
    seenCount: 0,
    unSeenCount: 0,
    responseCount: 0,
  });
  const [eventAndMessageData, setEventAndMessageData] = useState<any>(null);
  const [pieChartData, setPieChartData] = useState<any[]>([]);
  const [paginationModel, setPaginationModel] = useState<PaginationType>({
    page: 0,
    pageSize: 5,
  });
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);
  //To do
  const [loading, setLoading] = useState<boolean>(false);

  const handleViewClick = ({
    eventId,
    messageId,
  }: {
    eventId: number;
    messageId: number;
  }) => {
    setOpenPieChart(true);
    fetchEventPieChartData({ eventId, messageId });
  };

  const handleCloseDrawer = () => {
    setOpenPieChart(false);
  };

  const columns: GridColDef[] = [
    { field: "event", headerName: "Event", flex: 1.3 },
    { field: "date", headerName: "Date & Time", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      renderCell: (params) => {
        return <Typography fontSize={14}>{params.value}</Typography>;
      },
    },
    { field: "sender", headerName: "Sender", flex: 1 },
    {
      field: "recipients",
      headerName: "Assigned Users",
      flex: 0.8,
      renderCell: (params) => (
        <Typography fontSize={14}>{params.value} Users</Typography>
      ),
    },
    {
      field: "responseRate",
      headerName: "Seen",
      flex: 0.7,
      renderCell: (params) => (
        <Typography fontSize={14}>{params.value}</Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      renderCell: (params) => (
        <Box
          sx={viewButtonStyles}
          onClick={() => {
            handleViewClick({
              eventId: params.row.id,
              messageId: params.row.messageId,
            });
          }}
        >
          <Typography sx={viewReciepientStyles}>View</Typography>
        </Box>
      ),
    },
  ];

  const getEventsOverviewData = async () => {
    const response = await getEventsOverview();
    setEventsOverviewData(response?.data);
  };

  const getMessagResponseRateData = async () => {
    const response = await getEventsMessaageResponseRate();
    setMessageResponseData(response.data);
  };

  const getEventsGridData = async () => {
    setLoading(true);
    try {
      const response = await getEventsDataGrid({
        page: paginationModel.page,
        size: paginationModel.pageSize,
      });
      if (response && response.eventMessages) {
        const updatedResponse = response.eventMessages.map((event: any) => ({
          id: event.eventId,
          event: event.eventName,
          date: formatFullDateTime(event.createdOn),
          status: event.status,
          messageId: event.messageId,
          sender: event.senderName,
          recipients: event.recipientsCount,
          responseRate: `${event.responseRate}%`,
        }));
        setEventAndMessageData(updatedResponse);
        setTotalRowsCount(response.totalCount);
      } else {
        setEventAndMessageData([]);
        setTotalRowsCount(0);
      }
    } catch (error) {
      console.error("Failed to fetch message statistics", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchEventPieChartData = async ({
    eventId,
    messageId,
  }: {
    eventId: number;
    messageId: number;
  }) => {
    try {
      const response = await getTagResponsesByEventMsgId({
        eventId,
        messageId,
      });
      if (response.status === 200) {
        setPieChartData(response.data);
      }
    } catch (error) {
      console.error("Error fetching pie chart data:", error);
    }
  };

  useEffect(() => {
    getEventsOverviewData();
    getMessagResponseRateData();
  }, []);

  useEffect(() => {
    getEventsGridData();
  }, [paginationModel]);

  return (
    <>
      <Box sx={{ ...mainContentStyles, pl: 3 }}>
        {" "}
        <Box sx={breadcrumbMainStyles}>
          <Typography
            onClick={() => navigate(BASE)}
            sx={breadcrumbIntitalStyles}
          >
            Dashboard
          </Typography>
          <Typography sx={breadcrumbActiveStyles}>
            / Event Message Dashboard
          </Typography>
        </Box>
        <Box sx={titleSectionStyles}>
          <Typography sx={titleStyles}>Event Message Dashboard</Typography>
          <Typography sx={titleDescriptionStyles}>
            Track and analyze event related messages{" "}
          </Typography>
        </Box>
        <Box sx={mainScrollContent}>
          {/* Grid Layout */}
          <Box sx={mainGridStyles}>
            {/* Grid 1 */}
            <Box sx={gridStyles}>
              <Typography sx={overviewHeader}>Events Overview</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <StatCard
                    label="Total Events"
                    value={eventsOverviewData.totalEvents}
                    backgroundColor="#e3f2fd"
                  />
                </Grid>
                <Grid item xs={6}>
                  <StatCard
                    label="Event Messages"
                    value={eventsOverviewData.totalEventMessages}
                    backgroundColor="#e3f2fd"
                  />
                </Grid>
              </Grid>
              <Box display="flex">
                <PieChart
                  series={[
                    {
                      data: [
                        {
                          id: 0,
                          value: eventsOverviewData.upcomingEvents,
                          label: "Upcoming",
                          color: "#d9833d",
                        },
                        {
                          id: 1,
                          value: eventsOverviewData.pastEvents,
                          label: "Past",
                          color: "#9ca3af",
                        },
                        {
                          id: 2,
                          value: eventsOverviewData.activeEvents,
                          label: "Active",
                          color: "#4ade80",
                        },
                      ],
                    },
                  ]}
                  width={300}
                  height={300}
                  slotProps={{
                    legend: {
                      position: {
                        vertical: "bottom",
                        horizontal: "middle",
                      },
                      direction: "row",
                      itemGap: 30,
                      itemMarkWidth: 12,
                      itemMarkHeight: 12,
                    },
                  }}
                />
              </Box>
            </Box>
            {/* Grid 2 */}
            <Box sx={gridStyles}>
              <Typography sx={overviewHeader}>Message Response Rate</Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <StatCard
                    label="Recipient"
                    value={messageResponseData.recipientsCount}
                    backgroundColor="#f9fafb"
                  />
                </Grid>
                <Grid item xs={4}>
                  <StatCard
                    label="Seen"
                    value={messageResponseData.seenCount}
                    backgroundColor="#f0fdf4"
                    color="green"
                  />
                </Grid>
                <Grid item xs={4}>
                  <StatCard
                    label="Response"
                    value={messageResponseData.responseCount}
                    backgroundColor="#fef2f2"
                    color="red"
                  />
                </Grid>
              </Grid>
              <Box display="flex">
                <BarChart
                  xAxis={[{ scaleType: "band", data: ["Seen", "Reponse"] }]}
                  series={[
                    {
                      data: [messageResponseData.seenCount, 0],
                      color: "#4ade80",
                    },
                    {
                      data: [0, messageResponseData.responseCount],
                      color: "#f87171",
                    },
                  ]}
                  width={400}
                  height={300}
                  slotProps={{
                    legend: {
                      position: { vertical: "bottom", horizontal: "middle" },
                      labelStyle: {
                        fontSize: 12,
                        fill: "#333",
                      },
                      itemGap: 10,
                    },
                  }}
                  sx={{
                    "& .MuiChartsLegend-root": {
                      marginTop: "20px",
                      background: "red",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Grid 3 */}
            <Box gridColumn="1 / span 2">
              <Box>
                <Typography sx={overviewHeader} gutterBottom>
                  Events and Messages
                </Typography>
                <div style={{ height: 400, width: "100%" }}>
                  <UserAnalyticsTable
                    rows={eventAndMessageData}
                    columns={columns}
                    setPaginationModel={setPaginationModel}
                    paginationModel={paginationModel}
                    totalRowsCount={totalRowsCount}
                  />
                </div>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box p={3} pt={2}>
        {/* Header */}
        <Dialog open={openPieChart} onClose={handleCloseDrawer} fullWidth>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography sx={sectionTitleSx}>View Event Details</Typography>
            </Box>
            <CloseIcon
              sx={{ fontSize: 16, color: "#9e9e9e", cursor: "pointer" }}
              onClick={handleCloseDrawer}
            />
          </DialogTitle>
          <DialogContent sx={{ "&.MuiDialogContent-root": { padding: 0 } }}>
            <EventTagPieChart pieChartData={pieChartData} />
          </DialogContent>
        </Dialog>
      </Box>
    </>
  );
};

export default EventMessageDashboard;

const sectionTitleSx: SxProps = {
  color: "#020817",
  fontWeight: 600,
  fontSize: "18px",
};
